import { Button } from "@package/subframe/components/Button";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherCalendar } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRescheduleTaskInstanceCommandControllerReschedule } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";

interface RescheduleInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instance: ComplianceTaskInstanceResponseDto;
}

export function RescheduleInstanceDialog({
  open,
  onOpenChange,
  instance,
}: RescheduleInstanceDialogProps) {
  const queryClient = useQueryClient();
  const reschedule = useRescheduleTaskInstanceCommandControllerReschedule();
  const [busy, setBusy] = useState(false);

  const currentDueDate = format(new Date(instance.dueDate), "yyyy-MM-dd");
  const [dueDate, setDueDate] = useState(currentDueDate);

  // Re-seed the picker when the dialog opens for a (possibly different) instance.
  useEffect(() => {
    if (open) setDueDate(currentDueDate);
  }, [open, currentDueDate]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await reschedule.mutateAsync({
        instanceId: instance.id,
        data: { dueDate },
      });
      toast.success(
        `Due date moved to ${format(new Date(dueDate), "d MMM yyyy")}`,
      );
      await invalidateComplianceQueries(queryClient);
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(error, "Failed to reschedule the task"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">
          Reschedule compliance task instance
        </DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex w-full max-w-[448px] flex-col items-center gap-6 p-4">
            <IconWithLogoBackground
              variant="brand"
              size="x-large"
              icon={<FeatherCalendar />}
            />
            <div className="flex flex-col items-center gap-2">
              <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                Change due date
              </span>
              <span className="text-center text-body font-body text-subtext-color">
                Currently due {format(new Date(instance.dueDate), "d MMM yyyy")}
                . Pick the correct date for this task.
              </span>
            </div>
            <TextField className="h-auto w-full" size="large" label="Due date">
              <TextField.Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={busy}
              />
            </TextField>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              size="extra-large"
              onClick={handleConfirm}
              disabled={busy || !dueDate}
            >
              {busy ? "Saving..." : "Save new date"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
